import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, SITE_URL } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import SEO from '@/components/SEO';
import Breadcrumb from '@/components/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Wrench, ArrowLeft, Phone } from 'lucide-react';

export default function ReferenceDetail() {
    const { slug } = useParams();
    const { settings } = useSettings();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/public/projects/${slug}`).then(({ data }) => setProject(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
    }, [slug]);

    if (notFound) {
        return (
            <div className="sk-section">
                <div className="sk-container text-center">
                    <SEO title="Projekt nije pronađen" noindex />
                    <h1 className="font-display text-4xl">Projekt nije pronađen</h1>
                    <Button asChild className="mt-6"><Link to="/reference"><ArrowLeft className="mr-2 h-4 w-4" />Sve reference</Link></Button>
                </div>
            </div>
        );
    }

    if (loading || !project) {
        return (
            <div className="sk-section">
                <div className="sk-container space-y-4">
                    <Skeleton className="h-12 w-2/3" />
                    <Skeleton className="h-64 w-full rounded-2xl" />
                </div>
            </div>
        );
    }

    const seo = project.seo || {};
    const base = (settings?.site_url || SITE_URL).replace(/\/$/, '');
    return (
        <>
            <SEO
                title={seo.meta_title || `${project.title} – Škobić Keramika`}
                description={seo.meta_description || project.summary}
                canonical={seo.canonical_url || `${base}/reference/${project.slug}`}
                image={seo.og_image || project.featured_image}
            />
            <div className="sk-container pt-6">
                <Breadcrumb items={[
                    { name: 'Reference', url: '/reference' },
                    { name: project.title, url: `/reference/${project.slug}` },
                ]} />
            </div>

            <section className="sk-section pb-0">
                <div className="sk-container">
                    <div className="max-w-3xl">
                        <p className="sk-kicker">{project.type_of_work}</p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight">{project.title}</h1>
                        <p className="mt-4 text-lg text-foreground/75">{project.summary}</p>
                        <div className="mt-6 flex flex-wrap gap-3 text-sm text-foreground/70">
                            {project.location && (
                                <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5"><MapPin className="h-3.5 w-3.5" />{project.location}</span>
                            )}
                            {project.year && (
                                <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5"><Calendar className="h-3.5 w-3.5" />{project.year}</span>
                            )}
                            {project.type_of_work && (
                                <span className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5"><Wrench className="h-3.5 w-3.5" />{project.type_of_work}</span>
                            )}
                        </div>
                    </div>

                    <div className="mt-10 aspect-[16/9] rounded-2xl overflow-hidden border border-border">
                        <img src={project.featured_image} alt={project.title} className="h-full w-full object-cover" fetchpriority="high" />
                    </div>

                    {project.description && (
                        <div className="mt-12 max-w-3xl sk-prose">
                            <p>{project.description}</p>
                        </div>
                    )}

                    {project.gallery && project.gallery.length > 0 && (
                        <div className="mt-12">
                            <h2 className="font-display text-3xl mb-6">Galerija</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {project.gallery.map((g, i) => (
                                    <div key={i} className="aspect-[4/3] rounded-2xl overflow-hidden border border-border">
                                        <img src={g.url} alt={g.alt || project.title} className="h-full w-full object-cover" loading="lazy" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-16 rounded-2xl border border-border bg-[color:var(--sk-surface-2)] p-8 text-center">
                        <h2 className="font-display text-3xl">Želite sličan projekt?</h2>
                        <p className="mt-2 text-foreground/70">Javite nam se – izlazimo na teren u Poreču i Istri.</p>
                        <div className="mt-5 flex flex-wrap gap-3 justify-center">
                            <Button asChild className="bg-[color:var(--sk-text)] text-white hover:bg-black rounded-xl">
                                <a href={settings?.phone_link || 'tel:+385977094158'}><Phone className="mr-2 h-4 w-4" />{settings?.phone || '+385 97 709 4158'}</a>
                            </Button>
                            <Button asChild variant="outline" className="rounded-xl bg-white">
                                <Link to="/kontakt">Pošalji upit</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
